/**
 * Centralized Branding Configuration
 *
 * Edit this single file to change ALL branding across the entire dashboard.
 * Colors, logos, and company name are all controlled from here.
 *
 * After editing, the entire app will reflect the new branding automatically.
 */

export interface BrandingConfig {
  company: {
    name: string;
    tagline?: string;
    copyright?: string;
  };
  logos: {
    primary: string;      // Main logo (e.g., "/logo.png")
    wordmark: string;     // Text/wordmark logo (e.g., "/wordmark.svg")
    favicon?: string;     // Optional favicon path
  };
  colors: {
    primary: string;      // Main brand color
    secondary: string;    // Secondary/gradient start color
    tertiary: string;     // Tertiary/gradient end color
    accent: string;       // Accent/highlight color
    ring: string;         // Focus ring color
    gradient: {
      start: string;
      end: string;
      angle: number;      // Gradient angle in degrees
    };
    chart: string[];      // Chart color palette (5+ colors recommended)
    device?: {
      ios: string;        // Color for iOS line in device breakdown charts
      android: string;    // Color for Android line in device breakdown charts
    };
    gender?: {
      male: string;       // Color for male in gender breakdown charts
      female: string;     // Color for female in gender breakdown charts
    };
    bar?: string;         // Performance bar color (appears black/dark)
    // Page background colors (light and dark mode)
    background?: {
      light: string;      // Background color for light mode (default: #f5f5f5)
      dark: string;       // Background color for dark mode (default: #0a0a0a)
    };
    // Card/panel background colors
    card?: {
      light: string;      // Card background for light mode (default: #ffffff)
      dark: string;       // Card background for dark mode (default: #171717)
    };
  };
  email: {
    headerGradientStart: string;
    headerGradientEnd: string;
    buttonBackground: string;
    accentText: string;
  };
}

/**
 * ============================================
 * EDIT BELOW TO CHANGE BRANDING
 * ============================================
 */
export const branding: BrandingConfig = {
  company: {
    name: "Limitless Ladies",
    tagline: "Sales Academy",
    copyright: "Limitless Ladies",
  },

  logos: {
    primary: "/ll-logo.png",
    wordmark: "/ll-logo.png",
    favicon: "/favicon.ico",
  },

  colors: {
    // Main brand colors - warm copper/bronze and burgundy palette
    primary: "#C17A3F",      // Warm copper/bronze - primary actions, active states
    secondary: "#D4956A",    // Light copper - gradient start
    tertiary: "#7B2D2D",     // Dark burgundy - gradient end
    accent: "#F5EDE4",       // Cream - highlights, badges
    ring: "#C17A3F",         // Copper - focus rings

    // Gradient definition
    gradient: {
      start: "#D4956A",      // Light copper
      end: "#7B2D2D",        // Dark burgundy
      angle: 135,
    },

    // Chart color palette — warm copper/earth tones
    chart: [
      "#C17A3F",  // copper — primary series
      "#D4956A",  // light copper — secondary series
      "#7B2D2D",  // dark burgundy — tertiary
      "#E8B48A",  // pale copper — fourth series
      "#9E4A4A",  // mid burgundy — fifth
      "#F5EDE4",  // cream — highlight
      "#B86830",  // deep copper — seventh
      "#5C1E1E",  // deep maroon — eighth
    ],

    // Device breakdown chart colors
    device: {
      ios:     "#C17A3F",  // copper — iOS
      android: "#7B2D2D",  // burgundy — Android
    },

    // Gender breakdown chart colors
    gender: {
      male:   "#C17A3F",  // copper — Male
      female: "#D4956A",  // light copper — Female
    },

    // Performance bar color
    bar: "#3D1A0A",

    // Page background colors
    background: {
      light: "#FAF5EF",   // Warm cream for light mode
      dark: "#160E08",    // Very dark warm brown for dark mode
    },

    // Card/panel background colors
    card: {
      light: "#ffffff",
      dark: "#231410",    // Dark warm brown for dark mode
    },
  },

  email: {
    headerGradientStart: "#D4956A",
    headerGradientEnd: "#7B2D2D",
    buttonBackground: "#C17A3F",
    accentText: "#C17A3F",
  },
};

// ============================================
// HELPER FUNCTIONS (do not edit)
// ============================================

/**
 * Generate CSS gradient string from config
 */
export function getGradientCSS(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `linear-gradient(${gradient.angle}deg, ${gradient.start} 0%, ${gradient.end} 100%)`;
}

/**
 * Generate Tailwind gradient classes
 * Returns: "from-[#11b2f8] to-[#504df9]"
 */
export function getGradientTailwind(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `from-[${gradient.start}] to-[${gradient.end}]`;
}

/**
 * Get chart color by index (wraps around if index exceeds array length)
 */
export function getChartColor(index: number): string {
  const chart = branding.colors.chart;
  if (chart.length === 0) return branding.colors.primary;
  return chart[index % chart.length] ?? branding.colors.primary;
}

/**
 * Inject all branding CSS variables into the document root
 * Called automatically by ThemeProvider
 */
export function injectBrandingCSS(): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const { colors } = branding;

  // Core brand colors
  root.style.setProperty("--brand-primary", colors.primary);
  root.style.setProperty("--brand-secondary", colors.secondary);
  root.style.setProperty("--brand-tertiary", colors.tertiary);
  root.style.setProperty("--brand-accent", colors.accent);
  root.style.setProperty("--brand-ring", colors.ring);

  // Gradient colors
  root.style.setProperty("--brand-gradient-start", colors.gradient.start);
  root.style.setProperty("--brand-gradient-end", colors.gradient.end);

  // Chart colors (injected after theme detection below)

  // Background colors (with defaults)
  const bgLight = colors.background?.light ?? "#f5f5f5";
  const bgDark = colors.background?.dark ?? "#0a0a0a";
  const cardLight = colors.card?.light ?? "#ffffff";
  const cardDark = colors.card?.dark ?? "#171717";

  root.style.setProperty("--brand-bg-light", bgLight);
  root.style.setProperty("--brand-bg-dark", bgDark);
  root.style.setProperty("--brand-card-light", cardLight);
  root.style.setProperty("--brand-card-dark", cardDark);

  // Also update the semantic color variables
  root.style.setProperty("--primary", colors.primary);
  root.style.setProperty("--ring", colors.ring);

  // Update background based on current theme
  const isDark = root.classList.contains("dark");
  const isCrimson = root.classList.contains("crimson");
  // Set background and card based on current theme
  // Must use inline style (not CSS class) to win the specificity battle
  if (isCrimson) {
    root.style.setProperty("--background", "transparent");
    root.style.setProperty("--card", "rgba(255, 255, 255, 0.93)");
    root.style.setProperty("--foreground", "#ffffff");
    root.style.setProperty("--card-foreground", "#1a0404");
    root.style.setProperty("--popover", "rgba(255, 255, 255, 0.97)");
    root.style.setProperty("--popover-foreground", "#1a0404");
    root.style.setProperty("--border", "rgba(255, 255, 255, 0.28)");
    root.style.setProperty("--muted-foreground", "rgba(255,255,255,0.75)");
  } else if (isDark) {
    root.style.setProperty("--background", bgDark);
    root.style.setProperty("--card", cardDark);
    // Green chart palette for dark mode
    const darkChartColors = ["#C17A3F", "#D4956A", "#E8B48A", "#7B2D2D", "#9E4A4A"];
    darkChartColors.forEach((color, i) => {
      root.style.setProperty(`--chart-${i + 1}`, color);
    });
  } else {
    // Default chart colors from branding config
    colors.chart.forEach((color, i) => {
      root.style.setProperty(`--chart-${i + 1}`, color);
    });
    root.style.setProperty("--background", bgLight);
    root.style.setProperty("--card", cardLight);
    root.style.removeProperty("--foreground");
    root.style.removeProperty("--card-foreground");
    root.style.removeProperty("--popover");
    root.style.removeProperty("--popover-foreground");
    root.style.removeProperty("--border");
    root.style.removeProperty("--muted-foreground");
  }
}

/**
 * Get the MongoDB collection prefix based on company name
 * Converts "SalesGrid" to "salesgrid_", "My Company" to "my_company_"
 */
export function getCollectionPrefix(): string {
  return branding.company.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") + "_";
}

/**
 * Get a prefixed collection name
 * Example: getCollectionName("users") returns "salesgrid_users"
 */
export function getCollectionName(baseName: string): string {
  return getCollectionPrefix() + baseName;
}

/**
 * Get a prefixed model name (PascalCase)
 * Example: getModelName("User") returns "SalesGrid_User"
 */
export function getModelName(baseName: string): string {
  const prefix = branding.company.name.replace(/[^a-zA-Z0-9]+/g, "");
  return `${prefix}_${baseName}`;
}
